/**
 * One operator, drawn.
 *
 * The whole of the product's avatar rendering goes through here, because there
 * is exactly one thing worth deciding centrally and it is easy to get wrong:
 * an integer scale. The figure is sixteen cells square, and a fractional scale
 * puts the cell edges on half-pixels, which turns a pixel drawing into a
 * slightly blurry pixel drawing — the one failure that makes the whole system
 * look cheap. So the caller asks for a size in CSS pixels and gets the nearest
 * whole multiple of sixteen.
 *
 * An `<img>` with a data URI rather than inline SVG, and the reason is the
 * member list: a room holds two hundred people and a page holds fifty
 * messages, so the same dozen figures are drawn over and over as it scrolls.
 * One element and one cached string per figure is cheaper than two hundred
 * elements of two hundred rects each, and the browser gets to treat it as a
 * picture — which is what it is (lib/avatar.ts, `toDataURI`, memoises).
 *
 * Theme-aware, because the figure is ink on nothing: it is drawn from the dark
 * ramp against a near-black page, and the light theme needs the other palette
 * rather than an inverted image. `useTheme` is the one hook here, and it is
 * the reason this is a component rather than a function returning a string.
 */
import { useMemo } from 'react';
import { parseSeed, plateOf, toDataURI, type Selection } from '../../lib/avatar';
import { useTheme } from '../../state/useTheme';
import { characterSVG, partKey } from '../../lib/avatar-character';

export interface OperatorProps {
  /** A seed, or a selection the builder is holding mid-edit. */
  seed: string | Selection;
  /** Wanted size in CSS pixels; snapped down to a whole multiple of 16. */
  size?: number;
  /**
   * The accessible name. Empty — the default — draws it as decoration, which
   * is right everywhere it sits beside the name it belongs to: a screen reader
   * that reads "picture of an operator, sam" is reading furniture aloud.
   */
  alt?: string;
  className?: string;
  /** Short profile animations; disable for catalogue tiles and still previews. */
  motion?: boolean;
}

export function Operator({ seed, size = 16, alt = '', className }: OperatorProps) {
  const { theme } = useTheme();
  const light = theme === 'light';
  const scale = Math.max(1, Math.round(size / 16));
  const px = 16 * scale;
  const sel = useMemo(() => (typeof seed === 'string' ? parseSeed(seed) : seed), [seed]);
  const src = toDataURI(sel, { scale, light });
  return <img className={className ? `operator ${className}` : 'operator'} src={src} width={px} height={px} alt={alt} draggable={false} />;
}

/**
 * The figure in its plate.
 *
 * The plate is the ninth rail and the only one the renderer does not draw: it
 * is the frame around the figure rather than part of it, so it belongs to the
 * container and inherits the surface it is standing on (`avatar/README.md`).
 * Still frames and three earned motion treatments live in styles/profile.css
 * under `[data-plate]`. Motion plays briefly on entry and settles. Catalogue
 * tiles opt out; the device's reduced-motion preference disables it in CSS.
 *
 * Used where a figure is shown as itself: the profile, the builder's preview.
 * Not in a message list, where sixteen pixels of frame around sixteen pixels
 * of person is noise.
 */
export function OperatorPlate({ seed, size = 128, alt = '', className, motion = true }: OperatorProps) {
  const sel = useMemo(() => (typeof seed === 'string' ? parseSeed(seed) : seed), [seed]);
  return (
    <span className={className ? `op-plate ${className}` : 'op-plate'} data-plate={plateOf(sel)} data-motion={motion ? 'on' : 'off'}>
      <Character seed={sel} size={size} alt={alt} motion={motion} />
    </span>
  );
}

/** Full-body profiles get articulated layers; small chat portraits stay cached images. */
export function Character({ seed, size = 160, alt = '', className, motion = true }: OperatorProps) {
  const { theme } = useTheme();
  const sel = useMemo(() => typeof seed === 'string' ? parseSeed(seed) : seed, [seed]);
  const scale = Math.max(1, Math.round(size / 32));
  const svg = useMemo(() => characterSVG(sel, { scale, light: theme === 'light' }), [sel, scale, theme]);
  return <span className={`operator character${className ? ` ${className}` : ''}`} role={alt ? 'img' : undefined}
    aria-label={alt || undefined} aria-hidden={alt ? undefined : true} data-pose={partKey(sel, 'pose')}
    data-prop={partKey(sel, 'prop')} data-motion={motion ? 'on' : 'off'} dangerouslySetInnerHTML={{ __html: svg }} />;
}

export default Operator;
